import { metersToPixels, normaliseRotation, type BoundingBox } from '@repo/front-end-utils';
import type { FloorPlacement, FloorPlannerCamera } from '@repo/frontend-types';
import { effectiveViewRotation } from '@repo/ui/planner';

/**
 * The calibration screen's plan pane, turned and fitted to the camera being calibrated
 * (`observer-autogen-calibration-plan-orientation`). Pure: the screen passes the answers to
 * `ReadOnlyCanvas` as `viewRotation` and `fitBounds`, and the keyboard crosshair reads the same angle.
 *
 * Plan geometry: metres, y-up, the camera facing `rotation` degrees anticlockwise from +x. The
 * camera lives in its floor's design, so it is in floor-local metres — the frame the canvas draws in
 * once the floor's placement is folded into the view.
 */

const isFull = (camera: FloorPlannerCamera) => (camera.fovAngle ?? 0) >= 360;

/**
 * Whether Camera view turns the plan: every camera but a 360° one, which has no facing (R3). The
 * screen names and announces the view by this same rule.
 */
export const cameraHasFacing = (camera: FloorPlannerCamera): boolean => !isFull(camera);

/**
 * The plan's view angle in Camera view: the camera's facing straight up the screen (R1).
 *
 * The stage draws `screen = T + R(ρ)·S·p` with `S = diag(z, −z)`, so the facing `(cos r, sin r)`
 * reaches the screen as `(cos(r − ρ), sin(ρ − r))`. Screen-up is `(0, −1)`, so `ρ = r − 90`. The
 * floor's placement does not enter: the camera is already in floor-local metres.
 *
 * A 360° camera has no facing, so it keeps the floor's placement angle, as Whole floor does (R3).
 */
export const cameraViewRotation = (
  camera: FloorPlannerCamera,
  placement: FloorPlacement,
): number =>
  isFull(camera) ? effectiveViewRotation(0, placement) : normaliseRotation(camera.rotation - 90);

/** Whether the camera has a coverage to fit: an angle, and a reach above 0 (R4). */
const hasCoverage = (camera: FloorPlannerCamera) =>
  typeof camera.fovAngle === 'number' &&
  typeof camera.detectionDistance === 'number' &&
  camera.detectionDistance > 0;

/**
 * The box Camera view fits, in world pixels: the camera's cone (or, at 360°, its circle), clipped to
 * the floor's own outline (R2, R3). `null` — fit the whole floor — when the camera has no reach or
 * angle (R4), there is no floor outline, or the cone lies wholly off it.
 */
export const cameraCoverageBoxPx = (
  camera: FloorPlannerCamera,
  floorBoundsPx: BoundingBox | null,
): BoundingBox | null => {
  if (!floorBoundsPx || !hasCoverage(camera)) return null;
  const reach = camera.detectionDistance as number;
  let box: BoundingBox;
  if (isFull(camera)) {
    box = {
      minX: camera.x - reach,
      maxX: camera.x + reach,
      minY: camera.y - reach,
      maxY: camera.y + reach,
    };
  } else {
    const fov = camera.fovAngle as number;
    // Normalised first, so the axis walk below is at most four steps whatever angle is stored.
    const from = normaliseRotation(camera.rotation) - fov / 2;
    // A sector's box is fixed by its apex, its two edges and every axis crossing inside the arc —
    // exact, so no sampling between them can widen it.
    const angles = [from, from + fov];
    for (let a = Math.ceil(from / 90) * 90; a < from + fov; a += 90) angles.push(a);
    const xs = [camera.x];
    const ys = [camera.y];
    for (const deg of angles) {
      const rad = (deg * Math.PI) / 180;
      xs.push(camera.x + Math.cos(rad) * reach);
      ys.push(camera.y + Math.sin(rad) * reach);
    }
    box = {
      minX: Math.min(...xs),
      maxX: Math.max(...xs),
      minY: Math.min(...ys),
      maxY: Math.max(...ys),
    };
  }
  const clipped = {
    minX: Math.max(metersToPixels(box.minX), floorBoundsPx.minX),
    maxX: Math.min(metersToPixels(box.maxX), floorBoundsPx.maxX),
    minY: Math.max(metersToPixels(box.minY), floorBoundsPx.minY),
    maxY: Math.min(metersToPixels(box.maxY), floorBoundsPx.maxY),
  };
  return clipped.minX < clipped.maxX && clipped.minY < clipped.maxY ? clipped : null;
};

/**
 * Match picture (`observer-autogen-calibration-camera-model`, R8): the plan turned so the fitted
 * camera's "up the picture" — its floor heading at the view's centre, degrees anticlockwise from +x
 * in floor-local metres — points up the plan pane. The same `ρ = r − 90` rule as Camera view.
 */
export const matchPictureRotation = (pictureUpHeading: number): number =>
  normaliseRotation(pictureUpHeading - 90);

/** The plan around a guided target, metres either side of it (guided calibration R6). */
export const TARGET_CONTEXT_HALF_M = 3;

/**
 * The box the plan pane fits around a guided target alone (target-framing R1): when the camera is
 * more than `CAMERA_FRAME_MAX_M` away, or has no plan position to frame
 * with it (findable-spots Inputs; guided calibration R6): about 6 m square centred on the target, in
 * world pixels.
 */
export const targetBoxPx = (target: { x: number; y: number }): BoundingBox => ({
  minX: metersToPixels(target.x - TARGET_CONTEXT_HALF_M),
  maxX: metersToPixels(target.x + TARGET_CONTEXT_HALF_M),
  minY: metersToPixels(target.y - TARGET_CONTEXT_HALF_M),
  maxY: metersToPixels(target.y + TARGET_CONTEXT_HALF_M),
});

/** The margin round the camera and the target while a corner is asked for (findable-spots R1). */
export const CAMERA_TARGET_PAD_M = 2;

/**
 * The box the plan pane fits while a corner is asked for (findable-spots R1.1): the camera's plan
 * position and the target together, padded on every side, in world pixels, so the person sees
 * which way the camera looks and where the corner sits from it.
 */
export const cameraAndTargetBoxPx = (
  camera: { x: number; y: number },
  target: { x: number; y: number },
): BoundingBox => ({
  minX: metersToPixels(Math.min(camera.x, target.x) - CAMERA_TARGET_PAD_M),
  maxX: metersToPixels(Math.max(camera.x, target.x) + CAMERA_TARGET_PAD_M),
  minY: metersToPixels(Math.min(camera.y, target.y) - CAMERA_TARGET_PAD_M),
  maxY: metersToPixels(Math.max(camera.y, target.y) + CAMERA_TARGET_PAD_M),
});

/** Arc points per wedge: smooth enough at any zoom the pane allows. */
const WEDGE_ARC_STEPS = 16;

/**
 * The camera drawn on the calibration plan (findable-spots R1.2), as a polygon in floor metres: its
 * view wedge out to `reach` along its plan heading and angle, or a circle for a 360° camera. The
 * read-only canvas draws no cameras of its own, so the screen draws this one as an outline marker.
 * Null for a camera with no finite position or a reach of 0 or less.
 */
export const cameraWedgeM = (
  camera: Pick<FloorPlannerCamera, 'x' | 'y' | 'rotation' | 'fovAngle'>,
  reach: number,
): Array<[number, number]> | null => {
  if (!Number.isFinite(camera.x) || !Number.isFinite(camera.y) || !(reach > 0)) return null;
  const fov = Math.min(360, Math.max(1, camera.fovAngle ?? 90));
  const at = (deg: number): [number, number] => {
    const rad = (deg * Math.PI) / 180;
    return [camera.x + Math.cos(rad) * reach, camera.y + Math.sin(rad) * reach];
  };
  if (fov >= 360) {
    return Array.from({ length: WEDGE_ARC_STEPS * 2 }, (_, k) =>
      at((360 * k) / (WEDGE_ARC_STEPS * 2)),
    );
  }
  const from = normaliseRotation(camera.rotation ?? 0) - fov / 2;
  const arc = Array.from({ length: WEDGE_ARC_STEPS + 1 }, (_, k) =>
    at(from + (fov * k) / WEDGE_ARC_STEPS),
  );
  return [[camera.x, camera.y], ...arc];
};

/** Half the size of the camera's position mark on the plan, in metres. */
const CAMERA_MARK_HALF_M = 0.3;

/**
 * The camera's position on the calibration plan (Copilot on #2470): a small diamond at its plan
 * point, drawn with the wedge so a 360° camera, whose circle has no apex, is still located.
 */
export const cameraMarkM = (camera: { x: number; y: number }): Array<[number, number]> => [
  [camera.x, camera.y - CAMERA_MARK_HALF_M],
  [camera.x + CAMERA_MARK_HALF_M, camera.y],
  [camera.x, camera.y + CAMERA_MARK_HALF_M],
  [camera.x - CAMERA_MARK_HALF_M, camera.y],
];

/**
 * How far the camera may be from the target and still be framed with it (target-framing R1). Further
 * away, framing both draws the machine to find too small, so the target is framed alone.
 */
export const CAMERA_FRAME_MAX_M = 10;

/**
 * The plan pane's box while a corner is asked for (target-framing R1): the camera and the target
 * together when the camera is within `CAMERA_FRAME_MAX_M`, else the target alone. `withCamera` says
 * which, so the screen can point to a camera left out of the frame.
 */
export const planFrameFor = (
  camera: { x: number; y: number },
  target: { x: number; y: number },
): { box: BoundingBox; withCamera: boolean } => {
  const placed = Number.isFinite(camera.x) && Number.isFinite(camera.y);
  const near = placed && Math.hypot(camera.x - target.x, camera.y - target.y) <= CAMERA_FRAME_MAX_M;
  return near
    ? { box: cameraAndTargetBoxPx(camera, target), withCamera: true }
    : { box: targetBoxPx(target), withCamera: false };
};

/** The arrow's length and its base's width, in metres. */
const TOWARD_ARROW_LENGTH_M = 0.6;
const TOWARD_ARROW_WIDTH_M = 0.6;
/** How far from the target the arrow's tip sits: half a metre inside the target's 3 m frame. */
export const TOWARD_ARROW_TIP_M = TARGET_CONTEXT_HALF_M - 0.5;

/**
 * The arrow that points to a camera left out of the frame (target-framing R2): a triangle in floor
 * metres on the line from the target to the camera, its tip `TOWARD_ARROW_TIP_M` from the target (inside the target's
 * frame, so it is never clipped at the pane's edge),
 * pointing at the camera. Null for a camera with no finite position or one on the target.
 */
export const towardCameraArrowM = (
  camera: { x: number; y: number },
  target: { x: number; y: number },
): Array<[number, number]> | null => {
  const dx = camera.x - target.x;
  const dy = camera.y - target.y;
  const length = Math.hypot(dx, dy);
  if (!Number.isFinite(length) || length === 0) return null;
  const ux = dx / length;
  const uy = dy / length;
  const tip: [number, number] = [
    target.x + ux * TOWARD_ARROW_TIP_M,
    target.y + uy * TOWARD_ARROW_TIP_M,
  ];
  const back = TOWARD_ARROW_TIP_M - TOWARD_ARROW_LENGTH_M;
  const half = TOWARD_ARROW_WIDTH_M / 2;
  return [
    tip,
    [target.x + ux * back - uy * half, target.y + uy * back + ux * half],
    [target.x + ux * back + uy * half, target.y + uy * back - ux * half],
  ];
};
